import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { api, apiError, formatApiError } from "@/lib/api";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || apiError(e));

// Správa firemního číselníku kategorií (§C). Přejmenování se propíše do dekorů i položek.
export function CategoryManager({ open, onOpenChange, cats, onChanged }) {
  const [rename, setRename] = useState(null); // {id, name}
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const doAdd = async () => {
    const n = newName.trim();
    if (!n || busy) return;
    setBusy(true);
    try { await api.post("/pasport-categories", { name: n }); setNewName(""); await onChanged(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  const doRename = async () => {
    const n = (rename?.name || "").trim();
    if (!n || busy) return;
    setBusy(true);
    try { await api.patch(`/pasport-categories/${rename.id}`, { name: n }); setRename(null);
      toast.success("Přejmenováno — změna se propsala do dekorů i položek."); await onChanged(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  const doDelete = async (c) => {
    if (busy) return;
    setBusy(true);
    try { await api.delete(`/pasport-categories/${c.id}`); await onChanged(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[480px] max-w-[calc(100vw-2rem)] rounded-[18px]" data-testid="cats-manage-dialog">
        <DialogHeader>
          <DialogTitle>Kategorie pasportu</DialogTitle>
          <DialogDescription>Zakládá se, přejmenovává a maže se tady; u položek a dekorů se jen vybírá.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-[46vh] overflow-y-auto">
          {cats.length === 0 && <p className="text-sm text-muted-foreground py-2">Zatím žádné kategorie — přidejte první.</p>}
          {cats.map((c) => {
            const used = (c.decors || 0) + (c.items || 0) > 0;
            return rename?.id === c.id ? (
              <div key={c.id} className="flex items-center gap-1.5 p-1" data-testid={`cat-row-${c.id}`}>
                <Input autoFocus value={rename.name} onChange={(e) => setRename({ ...rename, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") doRename(); if (e.key === "Escape") setRename(null); }}
                  className="h-10 flex-1" data-testid={`cat-rename-input-${c.id}`} />
                <Button size="sm" onClick={doRename} className="h-10 font-bold"><Check size={15} /></Button>
                <button type="button" onClick={() => setRename(null)} className="w-10 h-10 grid place-items-center text-muted-foreground"><X size={16} /></button>
              </div>
            ) : (
              <div key={c.id} className="flex items-center gap-2 rounded-[10px] border border-border px-3 py-2.5" data-testid={`cat-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[15px] truncate">{c.name}</div>
                  <div className="text-[12px] text-muted-foreground">{c.decors || 0} dekorů · {c.items || 0} položek</div>
                </div>
                <button type="button" onClick={() => setRename({ id: c.id, name: c.name })} title="Přejmenovat"
                  className="w-9 h-9 grid place-items-center rounded-[8px] text-muted-foreground hover:bg-secondary hover:text-brand" data-testid={`cat-rename-${c.id}`}><Pencil size={15} /></button>
                <button type="button" onClick={() => doDelete(c)} disabled={used}
                  title={used ? `Používá ${c.decors || 0} dekorů a ${c.items || 0} položek` : "Smazat"}
                  className="w-9 h-9 grid place-items-center rounded-[8px] text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed" data-testid={`cat-delete-${c.id}`}><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pt-3 border-t border-border mt-1">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doAdd(); }} placeholder="Nová kategorie" className="h-11 flex-1" data-testid="cat-add-input" />
          <Button onClick={doAdd} disabled={!newName.trim() || busy} className="h-11 font-bold" data-testid="cat-add">Přidat</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
